import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X, Mail, Phone, MapPin } from "lucide-react";
import BrandLogo from "../BrandLogo";
import { site, empresa, razonSocialVisible, direccionCompleta, telHref } from "../../content/siteData";

export const GREEN = "#1A3328";
export const GREEN_DEEP = "#0F241C";
export const GOLD = "#B8975F";
export const CREAM = "#F7F3EB";
export const INK = "#1C211F";

export function GoldFlourishes({ className = "" }) {
  return (
    <svg
      className={`pointer-events-none absolute inset-0 w-full h-full ${className}`}
      viewBox="0 0 1200 800"
      fill="none"
      aria-hidden
    >
      <path d="M-40 120 C 180 40, 320 200, 480 140 S 780 40, 920 160" stroke={GOLD} strokeWidth="1.1" opacity="0.35" />
      <path d="M 80 700 C 280 620, 420 760, 620 680 S 980 600, 1240 720" stroke={GOLD} strokeWidth="1.1" opacity="0.28" />
      <path d="M 1100 -20 C 980 80, 1080 220, 980 320" stroke={GOLD} strokeWidth="0.9" opacity="0.22" />
    </svg>
  );
}

export function BrandWordmark({ light = false }) {
  return (
    <div className="flex flex-col leading-none">
      <span
        className="font-display text-[22px] tracking-[0.22em] uppercase"
        style={{ color: light ? "#fff" : GREEN }}
      >
        Aequor
      </span>
      <span className="flex items-center gap-2 mt-1" style={{ color: GOLD }}>
        <span className="h-px w-4 bg-current opacity-80" />
        <span className="text-[9px] tracking-[0.35em] uppercase font-semibold">Group</span>
        <span className="h-px w-4 bg-current opacity-80" />
      </span>
    </div>
  );
}

export const NAV_LINKS = [
  { to: "/#modulos", label: "Plataforma" },
  { to: "/servicios", label: "Servicios" },
  { to: "/precios", label: "Precios" },
  { to: "/#contacto", label: "Contacto" },
];

export function PublicNavbar({ solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const filled = solid || scrolled;

  return (
    <header
      data-testid="landing-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        filled ? "bg-[#F7F3EB]/92 backdrop-blur-md border-b border-[#1A3328]/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-brand">
          <BrandLogo className="h-12" testid="nav-logo" />
          <span className="hidden sm:block">
            <BrandWordmark />
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9 text-[14px] font-medium" style={{ color: `${INK}cc` }}>
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="hover:text-[#1A3328] transition-colors relative after:absolute after:inset-x-0 after:-bottom-1 after:h-px after:bg-[#B8975F] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/agendar"
            data-testid="navbar-agendar-btn"
            className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-sm border transition-colors hover:bg-white/60"
            style={{ borderColor: `${GOLD}aa`, color: GREEN }}
          >
            Agendar asesoría
          </Link>
          <Link
            to="/login"
            data-testid="navbar-login-btn"
            className="inline-flex items-center gap-1.5 text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
            style={{ backgroundColor: GREEN, color: CREAM }}
          >
            Ingresar al panel <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid="navbar-mobile-menu-btn"
          className="md:hidden p-2 rounded-sm hover:bg-black/5"
          style={{ color: GREEN }}
          aria-label="Menú"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-[#1A3328]/10 px-6 py-4 space-y-3"
          style={{ backgroundColor: CREAM }}
          data-testid="navbar-mobile-dropdown"
        >
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block py-2 text-[15px]"
              style={{ color: `${INK}b3` }}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/agendar"
            onClick={() => setOpen(false)}
            className="block text-center text-sm font-medium px-4 py-2.5 rounded-sm mt-2 border"
            style={{ borderColor: `${GOLD}aa`, color: GREEN }}
          >
            Agendar asesoría
          </Link>
          <Link
            to="/login"
            className="block text-center text-sm font-medium px-4 py-2.5 rounded-sm mt-2"
            style={{ backgroundColor: GREEN, color: CREAM }}
          >
            Ingresar al panel
          </Link>
        </div>
      )}
    </header>
  );
}

export function CompanyContactList({ light = false, className = "" }) {
  const tel = telHref(empresa.telefono);
  const items = [
    { Icon: Mail, label: "Email", value: empresa.email, href: `mailto:${empresa.email}` },
    { Icon: Phone, label: "Teléfono", value: empresa.telefono, href: tel },
    { Icon: MapPin, label: "Dirección", value: direccionCompleta() },
  ];
  const valueColor = light ? "rgba(255,255,255,0.85)" : INK;
  return (
    <ul className={`space-y-5 text-[14px] ${className}`}>
      {items.filter((it) => it.value).map(({ Icon, label, value, href }) => (
        <li key={label} className="flex items-start gap-3">
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: light ? GOLD : GREEN, color: light ? GREEN_DEEP : CREAM }}
          >
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: GOLD }}>
              {label}
            </div>
            {href ? (
              <a href={href} className="font-medium hover:opacity-80" style={{ color: valueColor }}>
                {value}
              </a>
            ) : (
              <span className="font-medium" style={{ color: valueColor }}>{value}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function PublicFooter() {
  const year = new Date().getFullYear();
  const tel = telHref(empresa.telefono);
  const col = "text-[11px] font-semibold tracking-[0.2em] uppercase mb-4";
  return (
    <footer className="pt-16 pb-8" style={{ backgroundColor: GREEN_DEEP }} data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
          <div className="col-span-2">
            <div className="mb-4">
              <BrandLogo className="h-20" onDark testid="footer-logo" />
            </div>
            <BrandWordmark light />
            <p className="text-white/55 text-[13px] leading-relaxed max-w-[320px] mt-4">{site.lema}</p>
            <address className="not-italic text-white/65 text-[13px] leading-relaxed mt-5 space-y-1" data-testid="footer-company">
              <div className="text-white font-semibold">{razonSocialVisible}</div>
              {empresa.rut && <div>RUT {empresa.rut}</div>}
              {direccionCompleta() && <div>{direccionCompleta()}</div>}
              {empresa.email && (
                <div>
                  <a href={`mailto:${empresa.email}`} className="hover:text-white">{empresa.email}</a>
                </div>
              )}
              {empresa.telefono && (
                <div>
                  {tel ? <a href={tel} className="hover:text-white">{empresa.telefono}</a> : empresa.telefono}
                </div>
              )}
            </address>
          </div>

          <div>
            <div className={col} style={{ color: GOLD }}>Plataforma</div>
            <ul className="space-y-2.5 text-[13px] text-white/65">
              <li><Link to="/#modulos" className="hover:text-white">Módulos</Link></li>
              <li><Link to="/precios" className="hover:text-white">Planes y precios</Link></li>
              <li><Link to="/login" className="hover:text-white">Ingresar al panel</Link></li>
            </ul>
          </div>

          <div>
            <div className={col} style={{ color: GOLD }}>Empresa</div>
            <ul className="space-y-2.5 text-[13px] text-white/65">
              <li><Link to="/servicios" className="hover:text-white">Servicios profesionales</Link></li>
              <li><Link to="/agendar" className="hover:text-white">Agendar asesoría</Link></li>
              <li><Link to="/#latam" className="hover:text-white">Chile, Argentina y LatAm</Link></li>
              <li><Link to="/#contacto" className="hover:text-white">Contacto</Link></li>
            </ul>
          </div>

          <div>
            <div className={col} style={{ color: GOLD }}>Legal</div>
            <ul className="space-y-2.5 text-[13px] text-white/65">
              <li><Link to="/terminos" className="hover:text-white" data-testid="footer-terminos">Términos y condiciones</Link></li>
              <li><Link to="/privacidad" className="hover:text-white" data-testid="footer-privacidad">Política de privacidad</Link></li>
              <li><Link to="/reembolsos" className="hover:text-white" data-testid="footer-reembolsos">Reembolsos y cancelación</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-[12px] text-white/45 text-center sm:text-left">
            © {year} {razonSocialVisible}{empresa.razonSocial ? ` (${site.marca})` : ""}. Todos los derechos reservados.
          </p>
          <p className="text-[12px] text-white/45">Santiago, Chile · Atención a Chile, Argentina y Latinoamérica</p>
        </div>
      </div>
    </footer>
  );
}

/** Mueve la vista al ancla (#seccion) al cambiar de ruta, o arriba si no hay ancla. */
export function useScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = hash.slice(1);
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [pathname, hash]);
}

export function PublicPage({ children, title, description }) {
  useScrollToHash();
  useEffect(() => {
    if (title) document.title = `${title} | ${site.marca}`;
    if (description) {
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    }
  }, [title, description]);

  return (
    <div className="overflow-x-hidden min-h-screen flex flex-col" style={{ backgroundColor: CREAM, color: INK }}>
      <PublicNavbar solid />
      <main className="flex-1 pt-[72px]">{children}</main>
      <PublicFooter />
    </div>
  );
}

export function PageIntro({ eyebrow, title, children }) {
  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: GREEN }}>
      <GoldFlourishes className="opacity-40" />
      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 md:py-20 relative">
        {eyebrow && (
          <p className="text-[12px] tracking-[0.28em] uppercase font-semibold mb-3" style={{ color: GOLD }}>
            {eyebrow}
          </p>
        )}
        <h1 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">{title}</h1>
        {children && <div className="text-white/70 text-[16px] md:text-[17px] mt-5 max-w-3xl leading-relaxed">{children}</div>}
      </div>
    </section>
  );
}
